// src/FieldApi.ts
import { Store } from "@tanstack/store";
var uid = 0;
var FieldApi = class _FieldApi {
  constructor(opts) {
    this.options = {};
    this.mount = () => {
      const info = this.getInfo();
      info.instances[this.uid] = this;
      const unsubscribe = this.form.store.subscribe(() => {
        this.store.batch(() => {
          const nextValue = this.getValue();
          const nextMeta = this.getMeta();
          if (nextValue !== this.state.value) {
            this.store.setState((prev) => ({ ...prev, value: nextValue }));
          }
          if (nextMeta !== this.state.meta) {
            this.store.setState((prev) => ({ ...prev, meta: nextMeta }));
          }
        });
      });
      this.update(this.options);
      this.options.onMount?.(this);
      return () => {
        unsubscribe();
        delete info.instances[this.uid];
        if (!Object.keys(info.instances).length) {
          delete this.form.fieldInfo[this.name];
        }
      };
    };
    this.update = (opts) => {
      if (this.state.value === void 0) {
        const formDefault = opts.form.options.defaultValues?.[opts.name];
        if (opts.defaultValue !== void 0) {
          this.setValue(opts.defaultValue);
        } else if (formDefault !== void 0) {
          this.setValue(formDefault);
        }
      }
      if (this._getMeta() === void 0) {
        this.setMeta(this.state.meta);
      }
      this.options = opts;
    };
    this.getValue = () => {
      return this.form.getFieldValue(this.name);
    };
    this.setValue = (updater, options) => {
      this.form.setFieldValue(this.name, updater, options);
      this.validate("change", this.state.value);
    };
    this._getMeta = () => this.form.getFieldMeta(this.name);
    this.getMeta = () => this._getMeta() ?? {
      isValidating: false,
      isTouched: false,
      touchedErrors: [],
      errors: [],
      errorMap: {},
      ...this.options.defaultMeta
    };
    this.setMeta = (updater) => this.form.setFieldMeta(this.name, updater);
    this.getInfo = () => this.form.getFieldInfo(this.name);
    this.pushValue = (value) => this.form.pushFieldValue(this.name, value);
    this.insertValue = (index, value) => this.form.insertFieldValue(this.name, index, value);
    this.removeValue = (index) => this.form.removeFieldValue(this.name, index);
    this.swapValues = (aIndex, bIndex) => this.form.swapFieldValues(this.name, aIndex, bIndex);
    this.getSubField = (name) => new _FieldApi({
      name: `${this.name}.${name}`,
      form: this.form
    });
    this.validateSync = (value = this.state.value, cause) => {
      const { onChange, onBlur } = this.options;
      const validate = cause === "submit" ? void 0 : cause === "change" ? onChange : onBlur;
      if (!validate)
        return;
      const validationCount = (this.getInfo().validationCount || 0) + 1;
      this.getInfo().validationCount = validationCount;
      const doValidate = () => {
        if (this.options.validator && typeof validate !== "function") {
          return this.options.validator().validate(
            value,
            validate
          );
        }
        if (this.form.options.validator && typeof validate !== "function") {
          return this.form.options.validator().validate(
            value,
            validate
          );
        }
        return validate(
          value,
          this
        );
      };
      const error = normalizeError(doValidate());
      const errorMapKey = getErrorMapKey(cause);
      if (this.state.meta.errorMap[errorMapKey] !== error) {
        this.setMeta((prev) => ({
          ...prev,
          errorMap: {
            ...prev.errorMap,
            [getErrorMapKey(cause)]: error
          }
        }));
      }
      if (this.state.meta.errorMap[errorMapKey]) {
        this.cancelValidateAsync();
      }
    };
    this.__leaseValidateAsync = () => {
      const count = (this.getInfo().validationAsyncCount || 0) + 1;
      this.getInfo().validationAsyncCount = count;
      return count;
    };
    this.cancelValidateAsync = () => {
      this.__leaseValidateAsync();
      this.setMeta((prev) => ({
        ...prev,
        isValidating: false
      }));
    };
    this.validateAsync = async (value = this.state.value, cause) => {
      const {
        onChangeAsync,
        onBlurAsync,
        onSubmitAsync,
        asyncDebounceMs,
        onBlurAsyncDebounceMs,
        onChangeAsyncDebounceMs
      } = this.options;
      const validate = cause === "change" ? onChangeAsync : cause === "submit" ? onSubmitAsync : onBlurAsync;
      if (!validate)
        return [];
      const debounceMs = cause === "submit" ? 0 : (cause === "change" ? onChangeAsyncDebounceMs : onBlurAsyncDebounceMs) ?? asyncDebounceMs ?? 0;
      if (this.state.meta.isValidating !== true) {
        this.setMeta((prev) => ({ ...prev, isValidating: true }));
      }
      const validationAsyncCount = this.__leaseValidateAsync();
      const checkLatest = () => validationAsyncCount === this.getInfo().validationAsyncCount;
      if (!this.getInfo().validationPromise) {
        this.getInfo().validationPromise = new Promise((resolve, reject) => {
          this.getInfo().validationResolve = resolve;
          this.getInfo().validationReject = reject;
        });
      }
      if (debounceMs > 0) {
        await new Promise((r) => setTimeout(r, debounceMs));
      }
      const doValidate = () => {
        if (this.options.validator && typeof validate !== "function") {
          return this.options.validator().validateAsync(
            value,
            validate
          );
        }
        if (this.form.options.validator && typeof validate !== "function") {
          return this.form.options.validator().validateAsync(value, validate);
        }
        return validate(
          value,
          this
        );
      };
      if (checkLatest()) {
        const prevErrors = this.getMeta().errors;
        try {
          const rawError = await doValidate();
          if (checkLatest()) {
            const error = normalizeError(rawError);
            this.setMeta((prev) => ({
              ...prev,
              isValidating: false,
              errorMap: {
                ...prev.errorMap,
                [getErrorMapKey(cause)]: error
              }
            }));
            this.getInfo().validationResolve?.([...prevErrors, error]);
          }
        } catch (error) {
          if (checkLatest()) {
            this.getInfo().validationReject?.([...prevErrors, error]);
            throw error;
          }
        } finally {
          if (checkLatest()) {
            this.setMeta((prev) => ({ ...prev, isValidating: false }));
            delete this.getInfo().validationPromise;
          }
        }
      }
      return this.getInfo().validationPromise ?? [];
    };
    this.validate = (cause, value) => {
      if (!this.state.meta.isTouched)
        return [];
      const errorMapKey = getErrorMapKey(cause);
      const prevError = this.getMeta().errorMap[errorMapKey];
      this.validateSync(value, cause);
      const newError = this.getMeta().errorMap[errorMapKey];
      if (prevError !== newError) {
        if (!this.options.asyncAlways) {
          return this.state.meta.errors;
        }
      }
      return this.validateAsync(value, cause);
    };
    this.handleChange = (updater) => {
      this.setValue(updater, { touch: true });
    };
    this.handleBlur = () => {
      const prevTouched = this.state.meta.isTouched;
      if (!prevTouched) {
        this.setMeta((prev) => ({ ...prev, isTouched: true }));
        this.validate("change");
      }
      this.validate("blur");
    };
    this.form = opts.form;
    this.uid = uid++;
    this.name = opts.name;
    if (opts.defaultValue !== void 0) {
      this.form.setFieldValue(this.name, opts.defaultValue);
    }
    this.store = new Store(
      {
        value: this.getValue(),
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        meta: this._getMeta() ?? {
          isValidating: false,
          isTouched: false,
          touchedErrors: [],
          errors: [],
          errorMap: {},
          ...opts.defaultMeta
        }
      },
      {
        onUpdate: () => {
          const state = this.store.state;
          state.meta.errors = Object.values(state.meta.errorMap).filter(
            (val) => val !== void 0
          );
          state.meta.touchedErrors = state.meta.isTouched ? state.meta.errors : [];
          this.prevState = state;
          this.state = state;
        }
      }
    );
    this.state = this.store.state;
    this.prevState = this.state;
    this.options = opts;
  }
};
function normalizeError(rawError) {
  if (rawError) {
    if (typeof rawError !== "string") {
      return "Invalid Form Values";
    }
    return rawError;
  }
  return void 0;
}
function getErrorMapKey(cause) {
  switch (cause) {
    case "submit":
      return "onSubmit";
    case "change":
      return "onChange";
    case "blur":
      return "onBlur";
    case "mount":
      return "onMount";
  }
}
export {
  FieldApi
};
//# sourceMappingURL=FieldApi.js.map